import { useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChatScrollController } from './chat-scroll'

type NavigationMessage = { id: string; role: string; text: string; status?: string }
type TurnPreview = { id: string; label: string; summary: string; status: string }

/** A bounded excerpt of the existing answer, never another AI-generated claim. */
function excerpt(text: string, limit: number): string {
  const plain = text.slice(0, 12000)
    .replace(/^\s*#{1,6}\s*(?:依据|结论与建议|待确认事项|建议方案|回答|方案说明)\s*$/gm, '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/<<cite:[\s\S]*?(?:>>|$)|\[\d+\]/g, '')
    .replace(/^\s*(?:#{1,6}\s+|[-*+]\s+|\d+[.)、]\s*)/gm, '')
    .replace(/[*_`~]/g, '').replace(/\s+/g, ' ').trim()
  return plain.length > limit ? plain.slice(0, limit).trimEnd() + '…' : plain
}
function messagePreviews(messages: readonly NavigationMessage[]): Map<string, TurnPreview> {
  const result = new Map<string, TurnPreview>()
  let current: TurnPreview | undefined, answers = ''
  for (const message of messages) {
    if (message.role === 'user') {
      current = { id: message.id, label: excerpt(message.text, 240) || '图片或资料提问', summary: '暂无回答', status: '暂无回答' }
      answers = ''; result.set(current.id, current)
    } else if (message.role === 'assistant' && current) {
      answers = (answers + '\n' + message.text).slice(0, 12000)
      current.summary = excerpt(answers, 220) || (message.status === 'pending' ? '正在整理本轮回答…' : '暂无回答正文')
      current.status = message.status === 'pending' ? '正在生成' : message.status === 'failed' ? '本轮未完成' : ['partial', 'stopped'].includes(message.status ?? '') ? '回答未完成' : '回答已完成'
    }
  }
  return result
}

/** Shared reading/following behavior for knowledge Q&A and project conversations. */
export default function ChatMessageViewport({ children, className, streaming, label, messages,focusTurnId }: {
  children: ReactNode; className: string; streaming: boolean; label: string; messages?: readonly NavigationMessage[];focusTurnId?:string
}) {
  const shell = useRef<HTMLDivElement>(null), viewport = useRef<HTMLDivElement>(null), content = useRef<HTMLDivElement>(null)
  const controller = useRef<ChatScrollController | null>(null)
  const tooltip = useRef<HTMLDivElement>(null), previewAnchor = useRef<HTMLButtonElement | null>(null)
  const closeTimer = useRef<number | null>(null), touchStart = useRef<{ id: string; wasOpen: boolean } | null>(null)
  const tooltipId = useId()
  const [paused, setPaused] = useState(false)
  const [turns, setTurns] = useState<TurnPreview[]>([]), [activeTurn, setActiveTurn] = useState('')
  const [previewId, setPreviewId] = useState(''), [previewTop, setPreviewTop] = useState(8)
  const previews = useMemo(() => messagePreviews(messages ?? []), [messages])
  const previewIndex = turns.findIndex(turn => turn.id === previewId), preview = turns[previewIndex]
  const cancelClose = () => { if (closeTimer.current !== null) window.clearTimeout(closeTimer.current); closeTimer.current = null }
  const closePreview = () => { cancelClose(); setPreviewId(''); previewAnchor.current = null }
  const scheduleClose = () => { cancelClose(); closeTimer.current = window.setTimeout(closePreview, 160) }
  const positionPreview = () => {
    const host = shell.current, anchor = previewAnchor.current, card = tooltip.current
    if (!host || !anchor || !card) return
    const hostRect = host.getBoundingClientRect(), anchorRect = anchor.getBoundingClientRect()
    const available = hostRect.height || viewport.current?.clientHeight || 320
    setPreviewTop(Math.max(8, Math.min(anchorRect.top - hostRect.top - 28, available - card.getBoundingClientRect().height - 8)))
  }
  const openPreview = (id: string, anchor: HTMLButtonElement) => {
    cancelClose(); previewAnchor.current = anchor; setPreviewId(id)
  }
  const jumpTo = (id: string) => {
    const marker = [...(content.current?.querySelectorAll<HTMLElement>('[data-chat-turn]') ?? [])].find(element => element.dataset.chatTurn === id)
    if (marker) { controller.current?.jumpTo(marker); setActiveTurn(id) }
    closePreview()
  }
  useLayoutEffect(() => {
    const element = viewport.current, body = content.current
    if (!element || !body) return
    const scroll = new ChatScrollController(element, setPaused)
    controller.current = scroll
    setPaused(false)
    // ResizeObserver also catches late image/font/table layout changes.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => scroll.contentChanged())
    observer?.observe(element); observer?.observe(body)
    let positionFrame: number | null = null
    const updatePosition = () => {
      positionFrame = null
      const markers = [...body.querySelectorAll<HTMLElement>('[data-chat-turn]')]
      const line = element.getBoundingClientRect().top + Math.min(80, element.clientHeight / 4)
      let current = markers[0]?.dataset.chatTurn ?? ''
      for (const marker of markers) { if (marker.getBoundingClientRect().top <= line) current = marker.dataset.chatTurn ?? current }
      setActiveTurn(current)
    }
    const onScroll = () => { if (positionFrame === null) positionFrame = requestAnimationFrame(updatePosition) }
    element.addEventListener('scroll', onScroll, { passive: true })
    updatePosition()
    const resize = () => { scroll.contentChanged(); positionPreview() }
    window.addEventListener('resize', resize)
    return () => { cancelClose(); element.removeEventListener('scroll', onScroll); if (positionFrame !== null) cancelAnimationFrame(positionFrame); observer?.disconnect(); window.removeEventListener('resize', resize); scroll.destroy(); controller.current = null }
  }, [])
  useLayoutEffect(() => {
    const markers = [...(content.current?.querySelectorAll<HTMLElement>('[data-chat-turn]') ?? [])]
    const next = markers.map(marker => previews.get(marker.dataset.chatTurn!) ?? { id: marker.dataset.chatTurn!, label: excerpt(marker.dataset.chatLabel || marker.textContent || '提问', 240), summary: '暂无回答', status: '暂无回答' })
    setTurns(current => current.length === next.length && current.every((turn, index) => turn.id === next[index]!.id && turn.label === next[index]!.label && turn.summary === next[index]!.summary && turn.status === next[index]!.status) ? current : next)
    controller.current?.setStreaming(streaming)
    controller.current?.contentChanged()
  }, [children, streaming, previews])
  useLayoutEffect(() => {
    if (preview) positionPreview()
    else if (previewId) closePreview()
  }, [previewId, turns])
  // Finite catalog results should open at their first result, while token
  // streams keep the normal following/manual-reading behavior.
  useLayoutEffect(()=>{
    if(!focusTurnId)return
    const marker=[...(content.current?.querySelectorAll<HTMLElement>('[data-chat-turn]')??[])].find(item=>item.dataset.chatTurn===focusTurnId)
    if(marker)controller.current?.jumpTo(marker)
  },[focusTurnId])
  return <div ref={shell} className={`chat-scroll-shell${turns.length ? ' has-turns' : ''}`} onKeyDown={event => { if (event.key === 'Escape' && previewId) { event.preventDefault(); event.stopPropagation(); closePreview() } }}>
    {turns.length ? <nav className="chat-turn-nav" aria-label="对话轮次定位" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>{turns.map((turn, index) => <button key={turn.id} type="button" className={`${activeTurn === turn.id ? 'is-active' : ''}${previewId === turn.id ? ' is-previewing' : ''}`} aria-current={activeTurn === turn.id ? 'location' : undefined} aria-label={`定位第 ${index + 1} 轮：${turn.label}`} aria-describedby={previewId === turn.id ? tooltipId : undefined}
      onMouseEnter={event => openPreview(turn.id, event.currentTarget)} onFocus={event => openPreview(turn.id, event.currentTarget)} onBlur={event => { if (!event.currentTarget.parentElement?.contains(event.relatedTarget as Node)) closePreview() }}
      onPointerDown={event => { touchStart.current = event.pointerType === 'touch' ? { id: turn.id, wasOpen: previewId === turn.id } : null }} onClick={event => {
        const touch = touchStart.current; touchStart.current = null
        if (touch?.id === turn.id && !touch.wasOpen) { openPreview(turn.id, event.currentTarget); return }
        jumpTo(turn.id)
      }}><span aria-hidden="true" /></button>)}</nav> : null}
    {preview ? <div ref={tooltip} id={tooltipId} className="chat-turn-preview" role="tooltip" style={{ top: previewTop }} onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
      <div className="chat-turn-preview-meta"><span>第 {previewIndex + 1} 轮</span><span>{preview.status}</span></div>
      <strong className="chat-turn-preview-question">{preview.label}</strong>
      <p className="chat-turn-preview-answer">{preview.summary}</p>
      <small>点击左侧定位点跳转</small>
    </div> : null}
    <div ref={viewport} className={`${className} chat-scroll-viewport`} role="log" aria-label={label} aria-live="polite" aria-atomic="false" tabIndex={0}>
      <div ref={content} className="chat-scroll-content">{children}</div>
    </div>
    {paused ? <button type="button" className="chat-scroll-latest" aria-label="回到最新输出" onClick={() => controller.current?.jumpToLatest()}><span aria-hidden="true">↓</span>回到最新</button> : null}
  </div>
}
